import { useState, useEffect } from 'react';

type UseCountdownProps = {
  initialSeconds?: number;
  autoStart?: boolean;
};

type UseCountdownReturn = {
  countdown: number;
  isActive: boolean;
  start: (seconds?: number) => void;
  stop: () => void;
  reset: () => void;
  formatTime: () => string;
};

/**
 * Custom hook to handle countdown timer functionality
 * @param initialSeconds Initial seconds for countdown (default: 0)
 * @param autoStart Whether to start countdown automatically (default: false)
 * @returns Object with countdown state and control functions
 */
export const useCountdown = ({
  initialSeconds = 0,
  autoStart = false,
}: UseCountdownProps = {}): UseCountdownReturn => {
  const [countdown, setCountdown] = useState(initialSeconds);
  const [isActive, setIsActive] = useState(autoStart);

  useEffect(() => {
    let timer: NodeJS.Timeout; // setTimeout returns a NodeJS.Timeout

    if (isActive && countdown > 0) {
      timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    } else if (countdown === 0 && isActive) {
      setIsActive(false);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [countdown, isActive]);

  const start = (seconds?: number) => {
    if (seconds !== undefined) {
      setCountdown(seconds);
    }
    setIsActive(true);
  };

  const stop = () => {
    setIsActive(false);
  };

  const reset = () => {
    setCountdown(initialSeconds);
    setIsActive(false);
  };

  const formatTime = () => {
    const minutes = Math.floor(countdown / 60);
    const seconds = countdown % 60;
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  return {
    countdown,
    isActive,
    start,
    stop,
    reset,
    formatTime,
  };
};
